import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getOrders, getOrder, getOrderNotes, updateOrderStatus, addOrderNote, createRefund, bulkUpdateOrders } from '@/services/orders.api';
import { showSuccess, showError } from '@/utils/toast';
import { getErrorMessage } from '@/services/api';
import type { OrderStatus } from '@wooconnect/shared';

interface OrderFilters {
  page?: number;
  perPage?: number;
  status?: string;
  search?: string;
  after?: string;
  before?: string;
}

export function useOrders(siteId: string | null, filters: OrderFilters) {
  return useQuery({
    queryKey: ['orders', siteId, filters],
    queryFn: () => getOrders(siteId!, filters),
    enabled: !!siteId,
    staleTime: 30 * 1000,
  });
}

export function useOrderDetail(siteId: string | null, orderId: number | null) {
  return useQuery({
    queryKey: ['order', siteId, orderId],
    queryFn: () => getOrder(siteId!, orderId!),
    enabled: !!siteId && !!orderId,
  });
}

export function useOrderNotes(siteId: string | null, orderId: number | null) {
  return useQuery({
    queryKey: ['order-notes', siteId, orderId],
    queryFn: () => getOrderNotes(siteId!, orderId!),
    enabled: !!siteId && !!orderId,
  });
}

export function useUpdateOrderStatus(siteId: string) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, status }: { orderId: number; status: OrderStatus }) =>
      updateOrderStatus(siteId, orderId, status),
    onSuccess: (_, { orderId }) => {
      showSuccess(t('common.success'));
      qc.invalidateQueries({ queryKey: ['orders', siteId] });
      qc.invalidateQueries({ queryKey: ['order', siteId, orderId] });
      qc.invalidateQueries({ queryKey: ['dashboard', siteId] });
    },
    onError: (err) => showError(getErrorMessage(err)),
  });
}

export function useAddOrderNote(siteId: string) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, note, customerNote }: { orderId: number; note: string; customerNote?: boolean }) =>
      addOrderNote(siteId, orderId, note, customerNote),
    onSuccess: (_, { orderId }) => {
      showSuccess(t('common.success'));
      qc.invalidateQueries({ queryKey: ['order-notes', siteId, orderId] });
    },
    onError: (err) => showError(getErrorMessage(err)),
  });
}

export function useCreateRefund(siteId: string) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, amount, reason }: { orderId: number; amount: string; reason?: string }) =>
      createRefund(siteId, orderId, amount, reason),
    onSuccess: (_, { orderId }) => {
      showSuccess(t('common.success'));
      qc.invalidateQueries({ queryKey: ['orders', siteId] });
      qc.invalidateQueries({ queryKey: ['order', siteId, orderId] });
    },
    onError: (err) => showError(getErrorMessage(err)),
  });
}

export function useBulkUpdateOrders(siteId: string) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderIds, status }: { orderIds: number[]; status: OrderStatus }) =>
      bulkUpdateOrders(siteId, orderIds, status),
    onSuccess: () => {
      showSuccess(t('common.success'));
      qc.invalidateQueries({ queryKey: ['orders', siteId] });
      qc.invalidateQueries({ queryKey: ['dashboard', siteId] });
    },
    onError: (err) => showError(getErrorMessage(err)),
  });
}
